import { add, format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';

import ReviewList from '~/components-ds/ReviewList';
import { BUSINESS_DETAILS_ROUTE } from '~/constants/routes';
import { useAppContent, useBusinessDetails } from '~/hooks';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { UnderwritingStatus } from '~/types/refData';
import { DateFormat, utcDateFormatter } from '~/utils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';
import { isDefined } from '~/utils/isDefined';

const ReviewBusinessDetails: React.FC = () => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.review.fields.businessDetails' });

  const {
    coverStart,
    occupation,
    employeeCount,
    annualTurnover,
    businessAddress,
    addressLine1,
    suburb,
    state,
    postcode,
    propertyValue,
  } = useBusinessDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const coverStartFormatted = coverStart && utcDateFormatter(coverStart, DateFormat.doMMMMyyyy);

  const coverExpiryDateFormatted =
    coverStartFormatted &&
    format(add(new Date(JSON.parse(coverStart)), { years: 1 }), DateFormat.doMMMMyyyy);

  const businessDetailsReviewList = [
    {
      label: getTextContent('coverStart'),
      data: coverStartFormatted,
    },
    {
      label: getTextContent('coverExpiry'),
      data: coverExpiryDateFormatted,
    },
    {
      label: getTextContent('occupation'),
      data: isPropertyOwner ? getTextContent('occupationIsPropertyOwner') : occupation?.occupation,
    },
    isPropertyOwner &&
      occupation?.underwritingStatus !== UnderwritingStatus.Decline && {
        label: getTextContent('tenantOccupation'),
        data: occupation?.occupation,
      },
    !isPropertyOwner && {
      label: getTextContent('employees'),
      data: employeeCount ? String(employeeCount) : null,
    },
    {
      label: isPropertyOwner ? getTextContent('rentalIncome') : getTextContent('businessTurnover'),
      data: currencyFormatter(annualTurnover, CurrencyFormat.Comma),
    },
    {
      label: isPropertyOwner
        ? getTextContent('propertyAddress')
        : getTextContent('businessAddress'),
      data: businessAddress
        ? businessAddress.partialAddress
        : `${addressLine1!}, ${suburb!}, ${state}, ${postcode}`,
    },
    isPropertyOwner
      ? {
          label: getTextContent('propertyValue'),
          data: currencyFormatter(propertyValue, CurrencyFormat.Comma),
        }
      : undefined,
  ].filter(isDefined);

  return (
    <ReviewList
      title={getTextContent('title')}
      list={businessDetailsReviewList.reduce(
        (prev, curr, idx) => ({
          ...prev,
          [String(idx)]: {
            id: idx,
            name: curr.label,
            label: curr.label,
            value: curr.data,
          },
        }),
        {}
      )}
      editRoute={BUSINESS_DETAILS_ROUTE}
    />
  );
};

export default ReviewBusinessDetails;
